<template>
  <div class="explore-layout" :class="{ 'explore-layout--active': hasTyped }">
    <div class="explore-wrapper" ref="wrapperEl">
      <div class="explore-input">
        <PredicateInputView :drawer-opens-up="segments.length > 0 || showingSyntax"
                            @predicate-input-changed="handlePredicateInputChanged"
                            @show-syntax-changed="handleShowingSyntaxChanged"
                            @home="router.push({ name: 'home' })"/>
      </div>
    </div>

    <div class="explore-board" :class="{ 'has-input': hasTyped }">
      <div v-if="guestLocked" class="explore-locked" role="status">
        <i class="ri-lock-line" aria-hidden="true"></i>
        <p><router-link to="/account">Log in</router-link> to see a world for this predicate</p>
      </div>
      <template v-else>
        <GraphView v-if="showingSyntax" :key="graphKey" :error-message="errorMessage" :graph="graph" :is-valid="isValid"/>
        <div v-if="hasTyped" v-show="!showingSyntax">
          <GameBoardView :error-message="errorMessage" :game-board-class="gameBoardClass" :is-valid="isValid"
                         :segments="segments" :board-key="boardKey"/>
        </div>
      </template>
    </div>

    <OrangeEasterEgg v-if="showOrangeEgg" @done="showOrangeEgg = false"/>
  </div>
</template>

<script setup>
import {computed, onMounted, ref, watch} from "vue";
import {useRouter} from "vue-router";
import {updateBoard, updateGridSize, isPortrait} from "@/composables/challenges";
import {useAuth} from "@/composables/useAuth";
import GameBoardView from "@/components/parts/GameBoardView.vue";
import PredicateInputView from "@/components/parts/PredicateInputView.vue";
import GraphView from "@/components/parts/GraphView.vue";
import OrangeEasterEgg from "@/components/parts/OrangeEasterEgg.vue";

const router = useRouter();
const {isLoggedIn} = useAuth();

const MIN_SEARCH_TERM_CHAR = 5;

let searchTerm = ref("");
let segments = ref([]);
let sentence = ref("Loading...");
let gameBoardClass = ref("game-board-dark");
let isValid = ref(false);
let errorMessage = ref("");
let graph = ref("");
let graphKey = ref(0);
let boardKey = ref(0);
let showingSyntax = ref(false);
let hasTyped = ref(false);
let showOrangeEgg = ref(false);
const wrapperEl = ref(null);

function getReservedHeight() {
  const landscapeMobile = !isPortrait() && window.innerHeight <= 639;
  if (!wrapperEl.value || landscapeMobile) return null;
  // On mobile portrait the tray is flex-ordered to the bottom, so its current
  // getBoundingClientRect().bottom oscillates between renders (small when no
  // board, nearly vh when a board is present). Use the tray height instead so
  // the reserved value is stable regardless of where the tray currently sits.
  if (isPortrait() && window.innerWidth < 640) {
    return wrapperEl.value.getBoundingClientRect().height + 8;
  }
  return wrapperEl.value.getBoundingClientRect().bottom + 8;
}

function handlePredicateInputChanged(value) {
  searchTerm.value = value;
  hasTyped.value = value.length > 0;
}

function handleShowingSyntaxChanged(value) {
  showingSyntax.value = value;
  if (!value) boardKey.value++;
}

onMounted(() => {
  updateGridSize(getReservedHeight());
});


// Guests can build predicates, but the /explore call needs an account —
// the board slot shows a locked notice where the world would appear.
const guestLocked = computed(() => !isLoggedIn.value && searchTerm.value.length > MIN_SEARCH_TERM_CHAR);

async function loadBoard() {
  errorMessage.value = "";
  await updateBoard(searchTerm, segments, sentence, isValid, errorMessage, gameBoardClass, graph, getReservedHeight());
  graphKey.value++;
  if (!showingSyntax.value) boardKey.value++;
  // The backend answers Orange(x) with an easter-egg message — celebrate it
  if (!isValid.value && /orange/i.test(errorMessage.value)) {
    showOrangeEgg.value = true;
  }
}

watch(searchTerm, async () => {
  gameBoardClass.value = "game-board-dark";
  if (searchTerm.value.length > MIN_SEARCH_TERM_CHAR && isLoggedIn.value) {
    await loadBoard();
  }
});

// Firebase restores sessions asynchronously — if auth arrives after the user
// already typed a predicate, fetch the board they were denied as a "guest".
watch(isLoggedIn, async (loggedIn) => {
  if (loggedIn && searchTerm.value.length > MIN_SEARCH_TERM_CHAR) {
    await loadBoard();
  }
});

</script>

<style scoped>
.explore-layout {
  display: contents;
}

.explore-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 2px;
  margin-top: clamp(24px, 18vh, 120px);
  transition: margin-top 0.45s var(--ease-out-quart, ease);
}

/* Collapse top margin when active — portrait only, not landscape */
@media not all and (max-height: 639px) and (orientation: landscape) {
  .explore-layout--active .explore-wrapper {
    margin-top: 24px;
  }
}

/* Portrait mobile: wrapper is at the bottom, top margin makes no sense */
@media screen and (max-width: 639px) and (orientation: portrait) {
  .explore-wrapper {
    margin-top: 6px;
    margin-bottom: 8px;
  }

  .explore-layout--active .explore-wrapper {
    margin-top: 6px;
  }
}

.explore-input {
  width: 100%;
  display: flex;
  justify-content: center;
  padding-bottom: 1px;
}

.explore-board {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-height: 0;
  opacity: 0;
  overflow: hidden;
  transition: max-height 0.45s var(--ease-out-quart, ease), opacity 0.3s ease;
}

.explore-board.has-input {
  max-height: 2000px;
  opacity: 1;
}

/* ── Guest lock: shown in the board slot where the world would appear ── */
.explore-locked {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin: 48px 16px 24px;
  padding: 20px 24px;
  max-width: 320px;
  border: 1px solid var(--border-color);
  text-align: center;
}

.explore-locked i {
  font-size: 22px;
  line-height: 1;
  color: var(--text-color);
  opacity: 0.6;
}

.explore-locked p {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: var(--text-color-bright);
}

.explore-locked a {
  color: var(--tomorrows-harvest);
}

@media screen and (max-height: 639px) and (orientation: landscape) {
  .explore-layout {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    height: 100vh;
    width: 100%;
  }

  .explore-board {
    order: 1;
    flex: 0 0 55%;
    align-items: flex-start;
    height: 100vh;
    max-height: none;
    opacity: 1;
    overflow: hidden;
    transition: none;
    /* Always reserve space in landscape so the input wrapper stays stable */
    display: flex;
    visibility: hidden;
  }

  .explore-board.has-input {
    visibility: visible;
  }

  /* Center the guest lock in the side-by-side board column */
  .explore-locked {
    margin: auto;
  }

  .explore-wrapper {
    order: 2;
    flex: 1 1 0;
    min-width: 0;
    height: 100vh;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 5px 10px 5px 8px;
    overflow-y: auto;
    margin-top: 0;
  }
}
</style>
